import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { Card, CardHeader, CardTitle, CardContent, Badge, Button } from '../components/ui';
import {
  Search,
  RefreshCw,
  MapPin,
  Loader2,
  Plus,
  Pencil,
  Trash2,
} from 'lucide-react';
import * as merchantPickupLocationsApi from '../services/merchantPickupLocations.api';
import type { MerchantPickupLocation, ActiveLocationType } from '../types/merchantPickupLocation';
import LocationFormModal from '../components/merchantLocations/LocationFormModal';
import { matchesSearch } from '../utils/search';
import { useCan } from '../contexts/useAuth';

type ErrorBody = { error?: string; requires_confirmation?: boolean };

export default function MerchantPickupLocations() {
  const can = useCan();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [editingLocation, setEditingLocation] = useState<MerchantPickupLocation | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [backendError, setBackendError] = useState<string | null>(null);
  const [requiresConfirmation, setRequiresConfirmation] = useState(false);

  const { data: locations, isLoading, isError, refetch } = useQuery({
    queryKey: ['merchantPickupLocations', showInactive, searchQuery],
    queryFn: () => merchantPickupLocationsApi.getMerchantPickupLocations(showInactive, searchQuery || undefined),
  });

  const createMutation = useMutation({
    mutationFn: merchantPickupLocationsApi.createMerchantPickupLocation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchantPickupLocations'] });
      setIsCreating(false);
      setBackendError(null);
      setRequiresConfirmation(false);
    },
    onError: (error: AxiosError<ErrorBody>) => {
      const data = error?.response?.data;
      if (data?.requires_confirmation) {
        setBackendError(data.error ?? null);
        setRequiresConfirmation(true);
      } else {
        setBackendError(data?.error || 'Failed to create merchant location');
        setRequiresConfirmation(false);
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ merchantId, data }: { merchantId: number; data: Parameters<typeof merchantPickupLocationsApi.updateMerchantPickupLocation>[1] }) =>
      merchantPickupLocationsApi.updateMerchantPickupLocation(merchantId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchantPickupLocations'] });
      setEditingLocation(null);
      setBackendError(null);
      setRequiresConfirmation(false);
    },
    onError: (error: AxiosError<ErrorBody>) => {
      const data = error?.response?.data;
      if (data?.requires_confirmation) {
        setBackendError(data.error ?? null);
        setRequiresConfirmation(true);
      } else {
        setBackendError(data?.error || 'Failed to update merchant location');
        setRequiresConfirmation(false);
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => merchantPickupLocationsApi.deleteMerchantPickupLocation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchantPickupLocations'] });
    },
  });

  const existingMerchantIds = useMemo(() => {
    if (!locations) return new Set<number>();
    return new Set(locations.map((l) => l.merchant_id));
  }, [locations]);

  const filteredLocations = useMemo(() => {
    if (!locations) return [];
    if (!searchQuery) return locations;
    // Normalised both sides — Core's merchant names carry doubled spaces that
    // the browser collapses on screen. See utils/search.ts.
    return locations.filter((l) =>
      matchesSearch(searchQuery, l.merchant_name, l.merchant_id.toString())
    );
  }, [locations, searchQuery]);

  const handleSave = (data: {
    merchantId?: number;
    merchantUid?: string;
    merchantName?: string;
    manualLocation?: { lat: number; lng: number; address_text?: string };
    activeType?: ActiveLocationType;
    force?: boolean;
  }) => {
    if (isCreating) {
      createMutation.mutate({
        merchantId: data.merchantId!,
        merchantUid: data.merchantUid!,
        merchantName: data.merchantName!,
        manualLocation: data.manualLocation,
        activeType: data.activeType,
        force: data.force,
      });
    } else if (editingLocation) {
      updateMutation.mutate({
        merchantId: editingLocation.merchant_id,
        data: {
          manualLocation: data.manualLocation,
          activeType: data.activeType,
          force: data.force,
        },
      });
    }
  };

  const handleDelete = (location: MerchantPickupLocation) => {
    if (confirm(`Are you sure you want to delete the location for "${location.merchant_name}"?`)) {
      deleteMutation.mutate(location._id);
    }
  };

  const formatLocation = (loc: { lat: number; lng: number } | null) => {
    if (!loc) return '-';
    return `${loc.lat.toFixed(4)}, ${loc.lng.toFixed(4)}`;
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Merchant Locations</h1>
          <p className="text-gray-500 mt-1">
            Manage merchant pickup locations (manual and automatic)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
          {can('merchants.manage') && (
            <Button onClick={() => setIsCreating(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Location
            </Button>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="mb-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by merchant name or ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <label className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-600">Show Inactive</span>
        </label>
      </div>

      <Card padding="none">
        <CardHeader className="px-4 pt-4">
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-blue-600" />
            Merchant Locations ({filteredLocations.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
              <span className="ml-2 text-gray-500">Loading merchant locations...</span>
            </div>
          ) : isError ? (
            <div className="text-center py-12 text-red-500">Failed to load merchant locations</div>
          ) : filteredLocations.length === 0 ? (
            <div className="text-center py-12">
              <MapPin className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No merchant locations configured</p>
              <p className="text-sm text-gray-500 mt-1">Click "Add Location" to create one</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Merchant</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active Location</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Manual</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Automatic</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Updated</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredLocations.map((loc) => (
                    <tr key={loc._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{loc.merchant_name}</div>
                        <div className="text-xs text-gray-500">ID: {loc.merchant_id}</div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge
                          variant={loc.active_type === 'manual' ? 'default' : 'success'}
                          className={loc.active_type === 'manual'
                            ? 'bg-blue-100 text-blue-700 border-blue-200'
                            : 'bg-green-100 text-green-700 border-green-200'}
                        >
                          {loc.active_type}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono">
                        {formatLocation(loc.active_location)}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 font-mono">
                        {formatLocation(loc.manual_location)}
                      </td>
<td className="px-4 py-3 text-xs text-gray-500 font-mono">
                        {formatLocation(loc.automatic_location)}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {new Date(loc.updated_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {can('merchants.manage') && (
                            <button
                              onClick={() => setEditingLocation(loc)}
                              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                              title="Edit"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                          )}
                          {can('merchants.manage') && (
                            <button
                              onClick={() => handleDelete(loc)}
                              className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                              title="Delete"
                              disabled={deleteMutation.isPending}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      {(isCreating || editingLocation) && (
        <LocationFormModal
          location={editingLocation}
          isNew={isCreating}
          onClose={() => {
            setIsCreating(false);
            setEditingLocation(null);
            setBackendError(null);
            setRequiresConfirmation(false);
          }}
          onSave={handleSave}
          isSaving={createMutation.isPending || updateMutation.isPending}
          existingMerchantIds={existingMerchantIds}
          backendError={backendError}
          requiresConfirmation={requiresConfirmation}
        />
      )}
    </div>
  );
}
